import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type Props<T> = {
  values: T;
  onChangePartial: (values: Partial<T>) => void;
};

/**
 * Debounces updates to the onChange function,
 * whilst providing immediate updates to the values consumed below.
 * This allows you to update UI in the current tree immediately
 * whilst debouncing updates to the parent hierarchy that does not require immediate updates.
 * It will also consume updates from the parent should values changed from the parent
 * The only caveat is that if parent sends updates within 500ms of the last local update,
 * the parent updates will be ignored as local updates are still in the dirty state (not emitted yet).
 * In the real world this probably won't happen but it could happen in a test environment.
 */
export const useDebouncedOnChange = <T extends Record<string, unknown>>({
  values,
  onChangePartial,
}: Props<T>) => {
  const isDirtyRef = useRef(false);
  const [immediateValues, setImmediateValues] = useState<T>(values);

  const emitChange = useCallback(
    (...args: Parameters<typeof onChangePartial>) => {
      onChangePartial(...args);
      isDirtyRef.current = false;
    },
    [onChangePartial]
  );

  const onChangeDebounced = useDebouncedCallback(emitChange, 500);
  useEffect(() => {
    // Do not sync values from the parent if we have local changes that haven't been emitted yet
    if (isDirtyRef.current) return;

    setImmediateValues(values);
  }, [values]);

  const handleChange = useCallback(
    (newValues: Partial<T>) => {
      isDirtyRef.current = true;
      setImmediateValues((currentValues) => ({
        ...currentValues,
        ...newValues,
      }));
      onChangeDebounced(newValues);
    },
    [onChangeDebounced]
  );

  return {
    handleChange,
    immediateValues,
  };
};
